import { useEffect, useState } from "react";
import { CheckCircle2, ExternalLink } from "lucide-react";
import { API } from "services/api";
import CustomButton from "components/CustomButton";
import StatCard from "components/StatCard";
import StatusBadge from "components/admin/StatusBadge";
import { Skeleton } from "components/ui/skeleton";

type AppStatusResponse = {
  ok: boolean;
  shop?: string | null;
  hasToken?: boolean;
  hasInfoShopify?: boolean;
  hasCarrier?: boolean;
  hasOrdersWebhook?: boolean;
  intrexConnected?: boolean;
  stats?: { totalCotacoes: number; entregues: number; emAndamento: number } | null;
};

export default function ShopifyHome() {
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [data, setData] = useState<AppStatusResponse | null>(null);

  useEffect(() => {
    let alive = true;

    async function carregar() {
      try {
        setLoading(true);
        setErro(null);
        const shop = new URLSearchParams(window.location.search).get("shop") || "";
        const resp = await API.get<AppStatusResponse>("/shopify/app-status", { params: { shop } });
        if (alive) setData(resp.data);
      } catch (e: any) {
        if (alive) {
          setErro(e?.response?.data?.erro || e?.response?.data?.message || e?.message || "Falha ao carregar status do app.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    carregar();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6 py-10">
      <div className="w-full max-w-2xl text-center">
        <img src="/images/intrex_logo.png" alt="Intrex" className="h-10 mx-auto mb-6" />

        {loading && (
          <div className="space-y-3 max-w-md mx-auto">
            <Skeleton className="h-5 w-40 mx-auto" />
            <Skeleton className="h-4 w-56 mx-auto" />
          </div>
        )}

        {!loading && erro && (
          <div className="max-w-md mx-auto">
            <h2 className="text-lg font-bold text-slate-900 mb-1">Intrex</h2>
            <p className="text-sm text-slate-500 mb-6">{erro}</p>
            <a href="/login" target="_blank" rel="noopener noreferrer">
              <CustomButton
                label="Abrir painel Intrex"
                icon={<ExternalLink className="h-4 w-4" />}
                iconDirection="right"
                className="w-full"
              />
            </a>
          </div>
        )}

        {!loading && !erro && data && (
          <div>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h2 className="text-lg font-bold text-slate-900 mb-1">Intrex Shipping conectado</h2>
            <p className="text-sm text-slate-500 mb-5">{data.shop || "Loja não identificada"}</p>

            <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
              <StatusBadge
                label={`Token salvo: ${data.hasToken ? "Sim" : "Não"}`}
                tone={data.hasToken ? "green" : "yellow"}
              />
              <StatusBadge
                label={`Frete internacional: ${data.hasCarrier ? "Ativo" : "Não ativo"}`}
                tone={data.hasCarrier ? "green" : "yellow"}
              />
              <StatusBadge
                label={`Webhook de pedidos: ${data.hasOrdersWebhook ? "Ativo" : "Não ativo"}`}
                tone={data.hasOrdersWebhook ? "green" : "yellow"}
              />
            </div>

            {data.stats && (
              <div className="grid grid-cols-3 gap-3 mb-6 text-left">
                <StatCard title="Cotações" subtitle="Total" value={data.stats.totalCotacoes} />
                <StatCard title="Em andamento" subtitle="Envios" value={data.stats.emAndamento} />
                <StatCard title="Entregues" subtitle="Envios" value={data.stats.entregues} />
              </div>
            )}

            <a href="/login" target="_blank" rel="noopener noreferrer">
              <CustomButton
                label="Abrir painel Intrex"
                icon={<ExternalLink className="h-4 w-4" />}
                iconDirection="right"
                className="w-full max-w-md"
              />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
